import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CompareArrows as MergeIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ArrowForward as ArrowIcon,
  AccountTree as BranchIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customerMergeApi } from '../api/client';
import { MergeCandidate, MergeCandidateCustomer, MergeConfidence } from '../types';
import { statusColors, statusLabels } from '../theme';

const confidenceConfig: Record<MergeConfidence, { label: string; color: 'error' | 'warning' | 'default' }> = {
  HIGH: { label: 'Hoj', color: 'error' },
  MEDIUM: { label: 'Middel', color: 'warning' },
  LOW: { label: 'Lav', color: 'default' },
};

const relCount = (c: MergeCandidateCustomer) =>
  (c._count?.contacts || 0) + (c._count?.activities || 0) + (c._count?.offers || 0) +
  (c._count?.transports || 0) + (c._count?.tasks || 0);

export const CustomerMerge: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [confidenceFilter, setConfidenceFilter] = useState<string | null>(null);
  const [mergeDialog, setMergeDialog] = useState<MergeCandidate | null>(null);
  const [branchDialog, setBranchDialog] = useState<MergeCandidate | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['merge-candidates', page, confidenceFilter],
    queryFn: async () => {
      const res = await customerMergeApi.getCandidates({
        page,
        limit: 50,
        confidence: confidenceFilter || undefined,
      });
      return res.data.data;
    },
  });

  const mergeMutation = useMutation({
    mutationFn: ({ primaryId, secondaryId }: { primaryId: string; secondaryId: string }) =>
      customerMergeApi.executeMerge(primaryId, secondaryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merge-candidates'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setMergeDialog(null);
      setSnackbar({ open: true, message: 'Kunder flettet', severity: 'success' });
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || 'Fletning fejlede',
        severity: 'error',
      });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: ({ customerIdA, customerIdB }: { customerIdA: string; customerIdB: string }) =>
      customerMergeApi.dismissMerge(customerIdA, customerIdB),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merge-candidates'] });
    },
  });

  const branchMutation = useMutation({
    mutationFn: ({ primaryId, secondaryId }: { primaryId: string; secondaryId: string }) =>
      customerMergeApi.makeBranch(primaryId, secondaryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merge-candidates'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setBranchDialog(null);
      setSnackbar({ open: true, message: 'Afdeling oprettet', severity: 'success' });
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || 'Kunne ikke oprette afdeling',
        severity: 'error',
      });
    },
  });

  const candidates: MergeCandidate[] = data?.candidates || [];
  const pagination = data?.pagination;
  const summary = data?.summary;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ fontSize: '0.75rem' }}>Kunne ikke hente merge-kandidater</Alert>;
  }

  const renderCustomerCell = (c: MergeCandidateCustomer) => (
    <Box>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{c.name}</Typography>
      <Box display="flex" gap={0.5} alignItems="center" flexWrap="wrap">
        {c.cvr && (
          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontFamily: 'monospace' }}>
            CVR: {c.cvr}
          </Typography>
        )}
        {c.city && (
          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
            {c.cvr ? ' · ' : ''}{c.city}
          </Typography>
        )}
      </Box>
      <Box display="flex" gap={0.5} mt={0.25}>
        <Chip
          label={statusLabels[c.status] || c.status}
          size="small"
          sx={{
            height: 16,
            fontSize: '0.55rem',
            fontWeight: 600,
            bgcolor: statusColors[c.status] || '#9e9e9e',
            color: 'white',
          }}
        />
        {relCount(c) > 0 && (
          <Typography sx={{ fontSize: '0.55rem', color: 'text.secondary', lineHeight: '16px' }}>
            {c._count?.transports || 0} transp. · {c._count?.offers || 0} tilbud · {c._count?.activities || 0} akt.
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderMergeDialog = () => {
    if (!mergeDialog) return null;
    const { primary, secondary } = mergeDialog;

    // Fields that will be copied from secondary to primary
    const fieldsToCopy: { label: string; value: string }[] = [];
    if (!primary.cvr && secondary.cvr) fieldsToCopy.push({ label: 'CVR', value: secondary.cvr });
    if (!primary.email && secondary.email) fieldsToCopy.push({ label: 'Email', value: secondary.email });
    if (!primary.phone && secondary.phone) fieldsToCopy.push({ label: 'Telefon', value: secondary.phone });
    if (!primary.address && secondary.address) fieldsToCopy.push({ label: 'Adresse', value: `${secondary.address}, ${secondary.city || ''}` });

    return (
      <Dialog open onClose={() => setMergeDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
          Flet kunder
        </DialogTitle>
        <DialogContent>
          <Box display="flex" gap={2} mb={2}>
            {/* Primary (keep) */}
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                <CheckIcon sx={{ fontSize: '0.9rem', color: 'success.main' }} />
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'success.main' }}>
                  Beholdes
                </Typography>
              </Box>
              <Card variant="outlined" sx={{ borderColor: 'success.main', borderWidth: 2 }}>
                <CardContent sx={{ p: '12px !important' }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{primary.name}</Typography>
                  <Box mt={0.5}>
                    {primary.cvr && <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>CVR: {primary.cvr}</Typography>}
                    {primary.email && <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Email: {primary.email}</Typography>}
                    {primary.phone && <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Tlf: {primary.phone}</Typography>}
                    {primary.address && <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Adresse: {primary.address}, {primary.city || ''}</Typography>}
                  </Box>
                  <Box mt={1} display="flex" gap={0.5} flexWrap="wrap">
                    <Chip label={`${primary._count?.contacts || 0} kontakter`} size="small" sx={{ height: 18, fontSize: '0.55rem' }} />
                    <Chip label={`${primary._count?.activities || 0} aktiviteter`} size="small" sx={{ height: 18, fontSize: '0.55rem' }} />
                    <Chip label={`${primary._count?.offers || 0} tilbud`} size="small" sx={{ height: 18, fontSize: '0.55rem' }} />
                    <Chip label={`${primary._count?.transports || 0} transporter`} size="small" sx={{ height: 18, fontSize: '0.55rem' }} />
                    <Chip label={`${primary._count?.tasks || 0} opgaver`} size="small" sx={{ height: 18, fontSize: '0.55rem' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Arrow */}
            <Box display="flex" alignItems="center">
              <ArrowIcon sx={{ fontSize: '1.5rem', color: 'text.secondary' }} />
            </Box>

            {/* Secondary (delete) */}
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                <CloseIcon sx={{ fontSize: '0.9rem', color: 'error.main' }} />
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'error.main' }}>
                  Slettes
                </Typography>
              </Box>
              <Card variant="outlined" sx={{ borderColor: 'error.main', borderWidth: 2 }}>
                <CardContent sx={{ p: '12px !important' }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{secondary.name}</Typography>
                  <Box mt={0.5}>
                    {secondary.cvr && <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>CVR: {secondary.cvr}</Typography>}
                    {secondary.email && <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Email: {secondary.email}</Typography>}
                    {secondary.phone && <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Tlf: {secondary.phone}</Typography>}
                    {secondary.address && <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>Adresse: {secondary.address}, {secondary.city || ''}</Typography>}
                  </Box>
                  <Box mt={1} display="flex" gap={0.5} flexWrap="wrap">
                    <Chip label={`${secondary._count?.contacts || 0} kontakter`} size="small" sx={{ height: 18, fontSize: '0.55rem' }} />
                    <Chip label={`${secondary._count?.activities || 0} aktiviteter`} size="small" sx={{ height: 18, fontSize: '0.55rem' }} />
                    <Chip label={`${secondary._count?.offers || 0} tilbud`} size="small" sx={{ height: 18, fontSize: '0.55rem' }} />
                    <Chip label={`${secondary._count?.transports || 0} transporter`} size="small" sx={{ height: 18, fontSize: '0.55rem' }} />
                    <Chip label={`${secondary._count?.tasks || 0} opgaver`} size="small" sx={{ height: 18, fontSize: '0.55rem' }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* What will happen */}
          <Alert severity="info" sx={{ fontSize: '0.7rem', '& .MuiAlert-message': { fontSize: '0.7rem' } }}>
            Alle relationer fra "{secondary.name}" flyttes til "{primary.name}" og derefter slettes "{secondary.name}".
            {fieldsToCopy.length > 0 && (
              <>
                <br />Felter der kopieres: {fieldsToCopy.map(f => `${f.label} (${f.value})`).join(', ')}
              </>
            )}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeDialog(null)} sx={{ fontSize: '0.7rem' }}>
            Annuller
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={mergeMutation.isPending}
            onClick={() => mergeMutation.mutate({
              primaryId: mergeDialog.primary.id,
              secondaryId: mergeDialog.secondary.id,
            })}
            sx={{ fontSize: '0.7rem' }}
          >
            {mergeMutation.isPending ? 'Fletter...' : 'Flet kunder'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <MergeIcon sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
        <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1rem' }}>
          Duplikerede kunder
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={2} sx={{ fontSize: '0.75rem' }}>
        Kunder der muligvis er den samme. Gennemga forslagene og flet eller afvis.
      </Typography>

      {/* Summary chips + filter */}
      <Box display="flex" gap={1} mb={2} alignItems="center">
        {summary && (
          <>
            <Chip
              label={`${summary.high} hoj`}
              color="error"
              size="small"
              sx={{ fontSize: '0.65rem', height: 22, fontWeight: 600 }}
            />
            <Chip
              label={`${summary.medium} middel`}
              color="warning"
              size="small"
              sx={{ fontSize: '0.65rem', height: 22, fontWeight: 600 }}
            />
            <Chip
              label={`${summary.low} lav`}
              size="small"
              sx={{ fontSize: '0.65rem', height: 22, fontWeight: 600 }}
            />
            <Box flex={1} />
          </>
        )}
        <ToggleButtonGroup
          value={confidenceFilter}
          exclusive
          onChange={(_, val) => { setConfidenceFilter(val); setPage(1); }}
          size="small"
          sx={{ '& .MuiToggleButton-root': { fontSize: '0.6rem', py: 0.25, px: 1, textTransform: 'none' } }}
        >
          <ToggleButton value={null as any}>Alle</ToggleButton>
          <ToggleButton value="HIGH">Hoj</ToggleButton>
          <ToggleButton value="MEDIUM">Middel</ToggleButton>
          <ToggleButton value="LOW">Lav</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {candidates.length === 0 ? (
        <Alert severity="success" sx={{ fontSize: '0.75rem' }}>
          Ingen duplikerede kunder fundet{confidenceFilter ? ` med ${confidenceConfig[confidenceFilter as MergeConfidence]?.label?.toLowerCase() || ''} konfidens` : ''}.
        </Alert>
      ) : (
        <Card sx={{ borderRadius: 1 }}>
          <CardContent sx={{ p: '8px !important' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, width: 80 }}>Konfidens</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, width: 160 }}>Arsag</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Kunde A (beholdes)</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Kunde B (slettes)</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', fontWeight: 600, width: 250 }}>Handling</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {candidates.map((candidate) => {
                    const conf = confidenceConfig[candidate.confidence];
                    return (
                      <TableRow key={candidate.id} hover>
                        <TableCell>
                          <Chip
                            label={conf.label}
                            color={conf.color}
                            size="small"
                            sx={{ fontSize: '0.6rem', height: 20, fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.7rem' }}>
                          {candidate.reason}
                        </TableCell>
                        <TableCell>{renderCustomerCell(candidate.primary)}</TableCell>
                        <TableCell>{renderCustomerCell(candidate.secondary)}</TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5}>
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              onClick={() => setMergeDialog(candidate)}
                              sx={{ fontSize: '0.6rem', borderRadius: 1, minWidth: 40 }}
                            >
                              Flet
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              color="info"
                              onClick={() => setBranchDialog(candidate)}
                              startIcon={<BranchIcon sx={{ fontSize: '0.7rem !important' }} />}
                              sx={{ fontSize: '0.6rem', borderRadius: 1, minWidth: 40 }}
                            >
                              Afdeling
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => dismissMutation.mutate({
                                customerIdA: candidate.primary.id,
                                customerIdB: candidate.secondary.id,
                              })}
                              disabled={dismissMutation.isPending}
                              sx={{ fontSize: '0.6rem', borderRadius: 1, minWidth: 40 }}
                            >
                              Afvis
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {pagination && pagination.totalPages > 1 && (
              <Box display="flex" justifyContent="center" gap={1} mt={1}>
                <Button
                  size="small"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  sx={{ fontSize: '0.65rem' }}
                >
                  Forrige
                </Button>
                <Typography sx={{ fontSize: '0.7rem', lineHeight: '30px' }}>
                  Side {page} af {pagination.totalPages}
                </Typography>
                <Button
                  size="small"
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage(page + 1)}
                  sx={{ fontSize: '0.65rem' }}
                >
                  Naeste
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {renderMergeDialog()}

      {/* Branch Dialog */}
      {branchDialog && (
        <Dialog open onClose={() => setBranchDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
            Opret afdeling
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ fontSize: '0.7rem', mb: 2, '& .MuiAlert-message': { fontSize: '0.7rem' } }}>
              "{branchDialog.secondary.name}" bliver en afdeling af "{branchDialog.primary.name}".
              Begge kunder beholdes — ingen data slettes.
            </Alert>
            <Box display="flex" gap={2} alignItems="center">
              <Box flex={1}>
                <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', mb: 0.5 }}>HOVEDKONTOR</Typography>
                <Card variant="outlined" sx={{ borderColor: 'primary.main' }}>
                  <CardContent sx={{ p: '8px !important' }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{branchDialog.primary.name}</Typography>
                    {branchDialog.primary.city && (
                      <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>{branchDialog.primary.city}</Typography>
                    )}
                  </CardContent>
                </Card>
              </Box>
              <BranchIcon sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
              <Box flex={1}>
                <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', mb: 0.5 }}>AFDELING</Typography>
                <Card variant="outlined" sx={{ borderColor: 'info.main' }}>
                  <CardContent sx={{ p: '8px !important' }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{branchDialog.secondary.name}</Typography>
                    {branchDialog.secondary.city && (
                      <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>{branchDialog.secondary.city}</Typography>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBranchDialog(null)} sx={{ fontSize: '0.7rem' }}>
              Annuller
            </Button>
            <Button
              variant="contained"
              color="info"
              disabled={branchMutation.isPending}
              onClick={() => branchMutation.mutate({
                primaryId: branchDialog.primary.id,
                secondaryId: branchDialog.secondary.id,
              })}
              sx={{ fontSize: '0.7rem' }}
            >
              {branchMutation.isPending ? 'Opretter...' : 'Opret afdeling'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ fontSize: '0.75rem' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerMerge;
